// Story 023 + SB6-ANA-DEF-01 — annotation drawer. Slides in from the right
// when `drawStore.draft != null`. Collects class + severity + location
// detail + chord% + radius + optional note, then POSTs to the contract
// endpoint.
//
// SB6-ANA-DEF-01 (2026-05-29 walkthrough) rework:
//   • Class — sourced from the active catalogue's `entry_type='defect_type'`
//     rows (via cataloguesStore + useDefectCatalogueQuery). The 5
//     hand-written MVP options were retired.
//   • Severity — options driven by the catalogue's class model. The "None"
//     model (`class_count = 0`) collapses to one disabled "—" and the form
//     submits with severity = null.
//   • Radius (m) — prefilled from the bbox centre via the existing
//     `radiusFromNormalizedCursor` pic2comp transform; helper text reads
//     "Distance from Rotor axis". User-editable.
//   • "Side" → "Location Detail" — options from the new
//     `listLocationDetailsByComponentGroup` endpoint, scoped to the active
//     data point's `componentGroupCode`. The hand-written
//     LE | TE | PS | SS enum was retired. Chord-% auto-lock survives by
//     matching on `typeCode === 'LEADING_EDGE' | 'TRAILING_EDGE'`.
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Snackbar,
  Stack,
  Switch,
  TextField,
  type TextFieldProps,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  DataPoint,
  Defect,
  DefectCatalogue,
  DefectCatalogueEntry,
  DefectCatalogueEntryV2,
  DefectValidation,
  ImageRef,
  LocalizedString,
  LocationDetail,
} from "@aeriosiq/types";
import {
  radiusFromNormalizedCursor,
  type Pic2CompGeometryInput,
  radius,
  typography,
  // SB6-ANA-UI-01 — the field-label / dense-input / menu / radio presets were
  // extracted to packages/ui from this file verbatim. Consume them here; the
  // local copies below are deleted. Zero visual change expected.
  fieldLabelSx,
  compactFieldSx,
  compactMenuProps,
  compactRadioLabelSlot,
} from "@aeriosiq/ui";
import { useDrawStore, centroidOfPath } from "../state/drawStore.js";
import { defectKeys } from "../../defects/queries.js";
import { useCataloguesStore } from "../../catalogues/cataloguesStore.js";
import { useDefectCatalogueQuery } from "../../catalogues/queries.js";
import { useDefectCatalogueV2Query } from "../../catalogues/queriesTwoAxis.js";
import { useDefectCollectionsQuery } from "../../catalogues/collectionsQueries.js";
import { partitionCollectionEntryIds } from "../../catalogues/collectionNarrowing.js";
import { useClassModelsQuery } from "../../catalogues/useClassModelsQuery.js";
import { prefillSeverity } from "../../catalogues/severityPrefill.js";
// SB6-ANA-PROG-01 — gate defect creation (a grading action) at the action
// layer until the dataset is ready. The drawer is already only reachable in
// editable contexts; this is defense-in-depth on the same gate the panels use.
import { isGradingReady } from "../state/analysisReadinessStore.js";
import { effectiveTypeEntryIds } from "../../catalogues/effectiveTypes.js";
import { useLocationDetailsByComponentGroupCode } from "../queries.locationDetails.js";
import { useComponentGroups } from "../queries.componentGroups.js";
import { componentGroupIdsInUse } from "../../catalogues/componentGroupScoping.js";
import {
  maxLocationDepth,
  locationOptionsAtLevel,
  locationAncestorPath,
  prefillLocationEntryId,
} from "../../catalogues/locationCascade.js";
import { pickLocalized } from "../../catalogues/localizedText.js";
import { XIcon } from "@aeriosiq/ui/icons";

// ANA-R3-GEOM-03 — distinct vertex count of a flat pixel path (drops the
// GeoJSON closing duplicate of a closed ring so a triangle reads "3 pts").
function distinctVertexCount(pathPx: number[]): number {
  const pairs = Math.floor(pathPx.length / 2);
  if (pairs < 2) return pairs;
  const closed =
    Math.abs((pathPx[0] ?? 0) - (pathPx[pathPx.length - 2] ?? 0)) < 1e-6 &&
    Math.abs((pathPx[1] ?? 0) - (pathPx[pathPx.length - 1] ?? 0)) < 1e-6;
  return closed ? pairs - 1 : pairs;
}

interface DefectDrawerProps {
  inspectionId: string;
  activeImage: ImageRef | null;
  /**
   * The data point matching `activeImage` (1:1 via `data_points.vis_image_id`).
   * Used to scope the Location-Detail dropdown by `componentGroupCode` and
   * to prefill `location_detail_id` on draft start.
   * `null` when no data point has been loaded yet — the dropdown then
   * renders empty and the form submits without a location-detail id.
   */
  activeDataPoint: DataPoint | null;
  /**
   * SB6-ANA-DA-03 — the DA's active class model id (override). Drives the
   * Severity radios INDEPENDENTLY of the catalogue's embedded `classModel`,
   * which is just a default. When null, the drawer falls back to the
   * catalogue's `classModel`.
   */
  activeClassModelId: string | null;
}

interface CreateDefectBody {
  defectClassId: string;
  severity: number | null;
  geometry: {
    path: number[];
    locationDetailId?: string | null;
    locationTypeCode?: string | null;
    chordPercent: number;
    radiusM: number;
  };
  note?: string | null;
  // DEFCAT P5 slice 2 (lyse@7.6.0) — fine catalogue location entry
  // (axis='location'). Top-level, additive; the BE pins the version + records
  // location_entry_id. The coarse location_detail_id stays in geometry.
  locationEntryId?: string | null;
  // DEFCAT FIX-3 (lyse@7.6.0) — drawer validation toggle; defaults to
  // 'definitely' server-side when omitted.
  validation?: DefectValidation | null;
}

// SB6-ANA-DEF-01 — auto-lock chord% on Leading/Trailing edge. Match by the
// i18n `abbreviation.en` short code (the stable 2-char convention in this
// domain) rather than `type_code` — phase4_global_catalogues.py normalises
// type codes to Title Case ("Leading Edge"), but the abbreviation is taken
// verbatim from the monolith `abbreviation` table so it stays "LE" / "TE".
// `name.en` is a case-insensitive fallback in case a tenant's seed lacks
// an abbreviation.
const LEADING_EDGE_ABBR = "LE";
const TRAILING_EDGE_ABBR = "TE";
const LEADING_EDGE_NAME_LC = "leading edge";
const TRAILING_EDGE_NAME_LC = "trailing edge";

// ---------------------------------------------------------------------------
// Typography hierarchy (CI brand book + analysis filter widget)
// ---------------------------------------------------------------------------
// The drawer's WHERE → WHAT → POSITION → NOTES structure needs a consistent
// hierarchy where every FIELD LABEL is VISIBLY SUBORDINATE to its SECTION
// HEADING. Christian's 2026-06-04 review: "Chord %" (a field label) read BIGGER
// than the section headings — an inverted hierarchy. Root cause was a raw-size
// collision: the headings used `label-md` (14) and so did the field labels
// (MUI's small InputLabel/FormLabel default ≈ 14–16). Equal size + the field
// labels' darker text → they out-shouted the headings.
//
// Fix per the brand book (02_TYPOGRAPHY.md §2.3 + 05_COMPONENTS.md §5.5): pull
// the two tiers APART on size AND colour AND case, never relying on one axis:
//
//   Tier 1 — SECTION HEADING (Where / What / Position / Notes)
//     size  = typography.size["label-md"]      (14)
//     weight= typography.weight.semibold       (600)
//     track = "0.04em"  (the brand-book §5.5 section-heading tracking)
//     color = text.secondary, textTransform: uppercase
//     Source: CI brand book 05_COMPONENTS.md §5.5 ("Section heading … 500 weight,
//     text.tertiary, ALL CAPS, 0.04em tracking") + the production analysis filter
//     widget's `LevelHeader`. ALL CAPS + tracking + semibold make it read as a
//     heading. It is the LARGEST label in each group.
//
//   Tier 2 — FIELD LABEL (Class, Severity, Validation, Radius (m), Chord %,
//            Notes, Location Detail, Component, Location)
//     size  = typography.size["caption-lg"]    (13)  ← strictly < Tier 1 (14)
//     weight= typography.weight.regular        (400) ← lighter than the heading
//     color = text.secondary                         ← quieter, never bolder/darker
//     Sentence-case (NOT caps). 13 < 14, regular < semibold, and no caps ⇒ the
//     field labels can never out-shout the headings on any axis. Applied via the
//     shared `fieldLabelSx` so the native `<Select>` InputLabel, the Autocomplete
//     TextField label, the Radio `<FormLabel>` legends (Severity/Validation) and
//     the Slider caption (Chord %) all share ONE field-label style. This is the
//     enforcement Christian asked for: "Sliders/radio-group legends use the same
//     field-label style as the TextField border labels."
//     (02_TYPOGRAPHY.md §2.3 `label.md`/`caption.lg`.)
//
//   Tier 3 — CAPTION / HELPER (collection name, geometry summary, helper text)
//     MUI `variant="caption"` / helperText, color text.secondary — the smallest
//     tier. (02_TYPOGRAPHY.md §2.3 `caption.lg`/`caption.sm`; §2.6 secondary copy
//     uses the text.secondary token, never opacity.)
//
// Round 3b (Christian 2026-06-04): the Tier-1 VISIBLE headings were removed
// again — with the dense scale the bordered cards group the fields clearly on
// their own, and the headings only cost vertical space. The group name lives
// on as each card's `aria-label` (region landmark), so screen readers keep the
// WHERE / WHAT / POSITION / NOTES structure.

// SB6-ANA-UI-01 — `fieldLabelSx`, `compactFieldSx`, `compactMenuProps`, and
// `compactRadioLabelSlot` were extracted verbatim to
// `packages/ui/src/theme/compact.ts` and are now imported from
// `@aeriosiq/ui` (see the import block above). The local copies that lived
// here are deleted; values are unchanged so the drawer renders identically.

// Item 1+2 (Christian 2026-06-04) — Asset-page field style for EVERY selection
// element: MUI OUTLINED (the default variant) with the label ALWAYS sitting on
// the upper border, even when no option is chosen. MUI only auto-shrinks an
// outlined `<InputLabel>` when the control has a value or focus; an empty Select
// otherwise drops the label INSIDE the box like a placeholder. The Asset pages
// (e.g. apps/web AddComponentDialog's date/Status fields) force the shrunk-on-
// border label via `inputLabel.shrink`. We replicate that on each native
// `<Select>` with the trio: InputLabel `shrink` (+ `fieldLabelSx`) + Select
// `notched` (so the border gap is cut for the floating label) + `displayEmpty`
// (so the empty value still renders the box, letting an in-field placeholder
// like "(not set)" sit BELOW the border label). The Class Autocomplete + the
// `Radius (m)`/`Notes` TextFields get the same `inputLabel: { shrink, sx }`.

// Item 6 (Christian 2026-06-04) — the WHERE / WHAT / POSITION / NOTES groups
// render as flat cards (muted fill, hairline border, no shadow).
//
// Round 3 (same day): card scale densified — radius.sm (6px), tight padding,
// 12px inter-group spacing ("reduce by 50%").
// Round 3b (same day): the visible headings + divider were removed — the
// bordered cards carry the grouping on their own. `heading` stays as the
// card's aria-label so the region landmarks survive for screen readers.
function SectionGroup({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <Box
      component="section"
      aria-label={heading}
      sx={{
        // Round 3c — nudged up from radius.sm (6px): 6 read a touch sharp on
        // a card this size, 12 (radius.base) was the sweet spot below the
        // original oversized 24.
        borderRadius: `${String(radius.base)}px`,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "action.hover",
        p: 1.25,
        // Round 3b — extra headroom: the first field's shrunk border-label
        // floats ~7px ABOVE its outline, so with uniform padding it visually
        // stuck to the card's top edge.
        pt: 2,
      }}
    >
      <Stack spacing={1.25}>{children}</Stack>
    </Box>
  );
}

// Round 3 (Christian 2026-06-04) — the matchers accept BOTH the coarse
// `LocationDetail` rows and catalogue location entries (`DefectCatalogueEntryV2`),
// which share the `name`/`abbreviation` LocalizedString shape. The chord
// auto-lock falls back to the CASCADE pick's own name when the coarse detail
// can't resolve (data point still loading, or a catalogue location without a
// `globalLocationDetailId` rollup) — "chord is 50% although the Location is
// Leading Edge" was exactly that gap.
interface EdgeNamed {
  name?: LocalizedString | null;
  abbreviation?: LocalizedString | null;
}

function isLeadingEdge(l: EdgeNamed | null): boolean {
  if (!l) return false;
  if (l.abbreviation?.en === LEADING_EDGE_ABBR) return true;
  return (l.name?.en ?? "").trim().toLowerCase() === LEADING_EDGE_NAME_LC;
}

function isTrailingEdge(l: EdgeNamed | null): boolean {
  if (!l) return false;
  if (l.abbreviation?.en === TRAILING_EDGE_ABBR) return true;
  return (l.name?.en ?? "").trim().toLowerCase() === TRAILING_EDGE_NAME_LC;
}

export function DefectDrawer({
  inspectionId,
  activeImage,
  activeDataPoint,
  activeClassModelId,
}: DefectDrawerProps): React.ReactElement {
  const draft = useDrawStore((s) => s.draft);
  const reset = useDrawStore((s) => s.reset);
  const setDraft = useDrawStore((s) => s.setDraft);
  const api = useApi();
  const queryClient = useQueryClient();

  // -- Active catalogue ---------------------------------------------------
  // Resolved by the EvaluationCataloguePicker / CataloguesBootstrap and
  // mirrored into the store. The drawer is read-only against the store
  // EXCEPT for the fallback: when the picker hasn't hydrated the store yet
  // (e.g. the DA's `active_catalogue_id` is NULL, or the picker hasn't
  // mounted) we fall back to the tenant's `isSystemDefault` catalogue, the
  // same fallback the BE's facets service uses (`resolveEffectiveCatalogueId`
  // in lyse-defect-type-facets.service.ts). Without this fallback the
  // drawer's Class dropdown + Severity radios are empty on a fresh DA.
  const activeCatalogueIdFromStore = useCataloguesStore(
    (s) => s.activeCatalogueId,
  );
  // Defensive `| undefined` casts — when the store is reset mid-render
  // (e.g. in tests with `useCataloguesStore.getState().reset()` between
  // renders) the selector can transiently return undefined before the
  // next schedule. Same hardening as CatalogueReadout uses.
  const availableCataloguesRaw = useCataloguesStore(
    (s) => s.availableCatalogues as typeof s.availableCatalogues | undefined,
  );
  const entriesByCatalogueIdRaw = useCataloguesStore(
    (s) => s.entries as typeof s.entries | undefined,
  );
  const availableCatalogues = useMemo(
    () => availableCataloguesRaw ?? [],
    [availableCataloguesRaw],
  );
  const entriesByCatalogueId = useMemo(
    () => entriesByCatalogueIdRaw ?? {},
    [entriesByCatalogueIdRaw],
  );
  const activeCatalogueId = useMemo<string | null>(() => {
    if (activeCatalogueIdFromStore) return activeCatalogueIdFromStore;
    const def = availableCatalogues.find((c) => c.isSystemDefault);
    return def?.catalogueId ?? availableCatalogues[0]?.catalogueId ?? null;
  }, [activeCatalogueIdFromStore, availableCatalogues]);
  // Drive the entries fetch from the drawer too — idempotent w.r.t. the
  // bootstrap; TanStack-Query dedupes on the cache key.
  useDefectCatalogueQuery(activeCatalogueId);

  const activeCatalogue: DefectCatalogue | null = useMemo(() => {
    if (!activeCatalogueId) return null;
    return (
      availableCatalogues.find((c) => c.catalogueId === activeCatalogueId) ??
      null
    );
  }, [availableCatalogues, activeCatalogueId]);

  const catalogueEntries: readonly DefectCatalogueEntry[] = useMemo(() => {
    if (!activeCatalogueId) return [];
    return entriesByCatalogueId[activeCatalogueId] ?? [];
  }, [entriesByCatalogueId, activeCatalogueId]);

  // -- Two-axis catalogue read (DEFCAT P5) --------------------------------
  // The rich shape carries `defaultSeverityByModel` (severity prefill) +
  // axis/parent (location picker, slice 2) + validity. We source the Class
  // dropdown from the `axis='type'` entries when the V2 read has landed and
  // fall back to the flat Story-033 entries until then, so a fresh DA still
  // shows defect types while the richer read is in flight.
  const v2Query = useDefectCatalogueV2Query(activeCatalogueId);
  const typeEntries = useMemo<readonly DefectCatalogueEntryV2[]>(() => {
    const v2 = v2Query.data?.entries ?? [];
    const types = v2.filter((e) => e.axis === "type");
    if (types.length > 0) return types;
    // Flat entries are structurally a subset of the V2 shape (they carry
    // entryId/name/defectClassId); the prefill helper tolerates the missing
    // `defaultSeverityByModel` by reading the bare `defectClassId`. The
    // generated entry types `axis` as `string | null` — narrow it to the V2
    // literal union (anything else is treated as axis-less).
    return catalogueEntries.map((e) => ({
      ...e,
      axis: e.axis === "type" || e.axis === "location" ? e.axis : null,
    }));
  }, [v2Query.data, catalogueEntries]);

  // -- Location axis + allocation gating (DEFCAT-R2-16 / R2-12) ------------
  // The drawer renders the catalogue's `axis='location'` tree as a top-down
  // CASCADE (L0 component group → one dropdown per deeper location level).
  // The defect records the FINE `location_entry_id` (the deepest level chosen);
  // the coarse `location_detail_id` (the 17, below) stays the data-point face
  // for chord/side. When the catalogue has no location axis the cascade hides.
  const allEntries = useMemo(() => v2Query.data?.entries ?? [], [v2Query.data]);
  const locationEntriesForGating = useMemo(
    () => allEntries.filter((e) => e.axis === "location"),
    [allEntries],
  );
  const validity = useMemo(() => v2Query.data?.validity ?? [], [v2Query.data]);
  // Per-axis structure labels (R2-15) — name the cascade dropdowns. The drawer
  // labels the L0 selector with the location level-0 label ("Component" /
  // "Component group"); deeper levels use their own label ("Location").
  const axisLevels = useMemo(
    () => v2Query.data?.axisLevels ?? [],
    [v2Query.data],
  );
  const locationLevelLabel = useCallback(
    (level: number, fallback: string): string => {
      const hit = axisLevels.find(
        (a) => a.axis === "location" && a.level === level,
      );
      const txt = hit ? pickLocalized(hit.label) : "";
      return txt.length > 0 ? txt : fallback;
    },
    [axisLevels],
  );
  // The deepest in-catalogue location level (0-based); -1 ⇒ no location axis.
  const deepestLocationLevel = useMemo(
    () => maxLocationDepth(allEntries),
    [allEntries],
  );
  // Type picker shows LEAF types (the selectable unit). Falls back to the
  // full list when the data carries no `isLeaf` info (flat pre-V2 entries).
  const typeLeaves = useMemo<readonly DefectCatalogueEntryV2[]>(() => {
    const leaves = typeEntries.filter((e) => e.isLeaf === true);
    return leaves.length > 0 ? leaves : typeEntries;
  }, [typeEntries]);

  // -- Class model (Severity grades) --------------------------------------
  // SB6-ANA-DA-03 — the DA's active class model id (override) takes
  // precedence over the catalogue's embedded default. SB6-ANA-DEF-01
  // (lyse@7.5.0) added `classes` to the listClassModels response so we can
  // resolve grades for ANY model — not just the catalogue's embedded one.
  const classModelsQuery = useClassModelsQuery();
  const effectiveClassModel = useMemo(() => {
    const overrideId = activeClassModelId;
    if (overrideId) {
      const found = classModelsQuery.data?.items.find(
        (m) => m.classModelId === overrideId,
      );
      if (found) return found;
    }
    // Fallback: the catalogue's embedded class model (e.g. when the DA
    // has no override set yet).
    return activeCatalogue?.classModel ?? null;
  }, [activeClassModelId, classModelsQuery.data, activeCatalogue]);
  // "None" model — class_count === 0. Resolution is by class_count, not by
  // a hard-coded UUID (project memory `reference_none_class_model_uuid`).
  const isNoneClassModel = effectiveClassModel?.classCount === 0;
  const severityChoices = useMemo(
    () =>
      [...(effectiveClassModel?.classes ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [effectiveClassModel],
  );

  // -- Location details (new endpoint) ------------------------------------
  const componentGroupCode = activeDataPoint?.componentGroupCode ?? null;
  const locationDetailsQuery =
    useLocationDetailsByComponentGroupCode(componentGroupCode);
  const locationDetails: readonly LocationDetail[] = useMemo(
    () => locationDetailsQuery.data?.items ?? [],
    [locationDetailsQuery.data],
  );

  // -- L0 component-group override (DEFCAT-FU-05 / D4a) -------------------
  // The data point preselects the component group (L0); the analyst can
  // re-target (e.g. a ROTOR_BLADE data point that also shows the Spinner).
  // The chosen group scopes the Location picker below.
  const componentGroupsQuery = useComponentGroups();
  const groupsInUse = useMemo(
    () => componentGroupIdsInUse(allEntries),
    [allEntries],
  );
  const componentGroupOptions = useMemo(
    () =>
      (componentGroupsQuery.data?.items ?? [])
        .filter((g) => groupsInUse.has(g.id))
        .map((g) => ({ id: g.id, label: pickLocalized(g.name) || g.groupCode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [componentGroupsQuery.data, groupsInUse],
  );

  // -- Form state ---------------------------------------------------------
  const [classId, setClassId] = useState<string>("");
  const [severity, setSeverity] = useState<number | null>(null);
  // DEFCAT P5 — once the analyst manually picks a Severity grade we stop
  // auto-prefilling, so changing the defect type or class model afterwards
  // never clobbers their choice. Reset per draft.
  const [severityTouched, setSeverityTouched] = useState<boolean>(false);
  const [locationDetailId, setLocationDetailId] = useState<string>("");
  // DEFCAT-R2-16 — the cascade selection: one location entry id per in-catalogue
  // level (index = depth). Empty string = "not chosen at this level". The FINE
  // `location_entry_id` submitted is the deepest non-empty entry.
  const [locationLevelIds, setLocationLevelIds] = useState<string[]>([]);
  // DEFCAT-FU-05 / R2-16 — the chosen L0 component group (top of the cascade).
  const [selectedComponentGroupId, setSelectedComponentGroupId] =
    useState<string>("");
  // One-time preselect guard: once the group is preselected or the analyst
  // touches it, the backfill never re-fires (so picking "All" sticks).
  const [componentGroupTouched, setComponentGroupTouched] =
    useState<boolean>(false);
  // R2-16 — one-time guard for the data-point → Location prefill (so re-running
  // the queries doesn't re-fire after the analyst clears/changes the cascade).
  const [locationPrefilled, setLocationPrefilled] = useState<boolean>(false);
  const [chordPercent, setChordPercent] = useState<number>(50);
  const [radiusM, setRadiusM] = useState<string>("");
  const [note, setNote] = useState<string>("");
  // DEFCAT-R2-17 — once the analyst types in Notes we never overwrite it with a
  // class default-description prefill. Reset per draft.
  const [noteDirty, setNoteDirty] = useState<boolean>(false);
  // DEFCAT FIX-3 — validation toggle; default 'definitely' (user may flip to
  // 'suspicion'). AI→defect conversion always lands 'definitely' (BE-side).
  const [validation, setValidation] = useState<DefectValidation>("definitely");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  // DEFCAT P5 slice 3 — when an active collection narrows the pickers, this
  // toggles back to the full catalogue. Reset per draft.
  const [showFullCatalogue, setShowFullCatalogue] = useState<boolean>(false);

  // -- Collection narrowing (DEFCAT P5 slice 3) ---------------------------
  // The active per-user collection narrows BOTH pickers to its entries (with
  // a toggle to the full catalogue). A collection may cover only one axis —
  // then the other axis is left un-narrowed (partitionCollectionEntryIds).
  const collectionsQuery = useDefectCollectionsQuery(activeCatalogueId);
  const activeCollection = useMemo(() => {
    const data = collectionsQuery.data;
    if (!data?.activeCollectionId) return null;
    return (
      data.items.find((c) => c.collectionId === data.activeCollectionId) ?? null
    );
  }, [collectionsQuery.data]);
  const collectionAxisIds = useMemo(() => {
    const typeIdSet = new Set(typeEntries.map((e) => e.entryId));
    const locIdSet = new Set(locationEntriesForGating.map((e) => e.entryId));
    return partitionCollectionEntryIds(
      activeCollection?.entryIds,
      typeIdSet,
      locIdSet,
    );
  }, [activeCollection, typeEntries, locationEntriesForGating]);
  // Narrowing is ON when a collection is active and the user hasn't toggled
  // to the full catalogue.
  const narrowingActive = activeCollection != null && !showFullCatalogue;

  // -- Cascade: per-level location options (DEFCAT-R2-16) -----------------
  // One dropdown per in-catalogue location level (0..deepestLocationLevel).
  // Level n options = entries at that depth, scoped to the chosen L0 and the
  // parent picked at level n-1. A collection covering the location axis narrows
  // the option set too. The deepest non-empty pick is the FINE location entry.
  const cascadeLevelOptions = useMemo(() => {
    if (deepestLocationLevel < 0 || !selectedComponentGroupId) return [];
    const out: {
      level: number;
      options: ReturnType<typeof locationOptionsAtLevel>;
    }[] = [];
    for (let level = 0; level <= deepestLocationLevel; level += 1) {
      const parent = level === 0 ? "" : (locationLevelIds[level - 1] ?? "");
      // A deeper level is only meaningful once its parent is chosen.
      if (level > 0 && parent.length === 0) {
        out.push({ level, options: [] });
        continue;
      }
      let opts = locationOptionsAtLevel(
        allEntries,
        selectedComponentGroupId,
        level,
        level === 0 ? null : parent,
      );
      if (narrowingActive && collectionAxisIds.locationIds.size > 0) {
        opts = opts.filter((o) => collectionAxisIds.locationIds.has(o.entryId));
      }
      out.push({ level, options: opts });
    }
    return out;
  }, [
    deepestLocationLevel,
    selectedComponentGroupId,
    locationLevelIds,
    allEntries,
    narrowingActive,
    collectionAxisIds,
  ]);
  // The FINE location entry = the deepest non-empty cascade pick.
  const locationEntryId = useMemo<string>(() => {
    for (let i = locationLevelIds.length - 1; i >= 0; i -= 1) {
      const id = locationLevelIds[i];
      if (id && id.length > 0) return id;
    }
    return "";
  }, [locationLevelIds]);

  // -- Type options gated by the chosen location (DEFCAT-R2-12 effective(L)) --
  // `null` from the helper means "ungated" (empty overlay, or no location
  // chosen yet) → show every leaf type. Otherwise narrow to the types in
  // effective(L) = own ∪ inherited − node-local exclusions (design note).
  const validTypeSet = useMemo(
    () =>
      effectiveTypeEntryIds(
        validity,
        locationEntriesForGating,
        typeEntries,
        locationEntryId.length > 0 ? locationEntryId : null,
      ),
    [validity, locationEntriesForGating, typeEntries, locationEntryId],
  );
  const typeOptions = useMemo<readonly DefectCatalogueEntryV2[]>(() => {
    let opts = validTypeSet
      ? typeLeaves.filter((e) => validTypeSet.has(e.entryId))
      : typeLeaves;
    // Collection narrowing (only when the collection covers the type axis).
    if (narrowingActive && collectionAxisIds.typeIds.size > 0) {
      opts = opts.filter((e) => collectionAxisIds.typeIds.has(e.entryId));
    }
    return opts;
  }, [typeLeaves, validTypeSet, narrowingActive, collectionAxisIds]);

  // -- Radius prefill -----------------------------------------------------
  // Compute on draft change. Same pic2comp transform `useRadiusReadout`
  // uses for the viewer readout, but driven by the bbox centre instead of
  // the cursor.
  const prefilledRadius = useMemo<number | null>(() => {
    if (!draft || !activeImage) return null;
    const imageW = activeImage.width;
    const imageH = activeImage.height;
    if (imageW <= 0 || imageH <= 0) return null;
    // ANA-R3-GEOM-03 (OQ2) — chord%/radius location derives from the polygon
    // CENTROID, not the bbox centre, so a non-convex shape locates correctly.
    const centroid = centroidOfPath(draft.pathPx);
    const nx = centroid.x / imageW;
    const ny = centroid.y / imageH;
    const geometry = (
      activeImage as unknown as { geometry?: Pic2CompGeometryInput | null }
    ).geometry;
    if (geometry == null) return null;
    const r = radiusFromNormalizedCursor(nx, ny, geometry);
    if (r !== null) return r;
    // Pre-6.3.0 fallback — image-level stand-in via |componentZ|.
    const cz = geometry.componentZ;
    return cz == null ? null : Math.abs(cz);
  }, [draft, activeImage]);

  // Reset form when a new draft starts. Severity gets a SECOND effect
  // below that seeds it once the catalogue's class model has loaded — the
  // catalogue fetch can land after the drawer opens, so seeding here only
  // (with whatever severityChoices currently is) would leave Severity
  // empty for the user on a fresh DA.
  //
  // Christian (2026-05-29): prefill the Location Detail from the active
  // data point's `location_detail_id` — that's where the picture was taken
  // so it's the right default. The data-points query and the location
  // -details query race the drawer open; if either lands after the draft
  // starts, the second effect below backfills.
  useEffect(() => {
    if (!draft) return;
    setClassId("");
    setLocationDetailId(activeDataPoint?.locationDetailId ?? "");
    setLocationLevelIds([]);
    setSelectedComponentGroupId("");
    setComponentGroupTouched(false);
    setLocationPrefilled(false);
    setShowFullCatalogue(false);
    setChordPercent(50);
    setNote("");
    setNoteDirty(false);
    setValidation("definitely");
    setErrorMsg(null);
    setSeverity(null);
    setSeverityTouched(false);
    setRadiusM(
      prefilledRadius != null && Number.isFinite(prefilledRadius)
        ? prefilledRadius.toFixed(2)
        : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  // Backfill Location Detail once the queries land (if the user hasn't
  // picked yet). Same one-time-per-draft pattern as Severity.
  useEffect(() => {
    if (!draft) return;
    if (locationDetailId.length > 0) return;
    const dpLocId = activeDataPoint?.locationDetailId;
    if (!dpLocId) return;
    // Only seed when the queried list actually contains the id — otherwise
    // the MUI Select shows an out-of-range selection.
    if (locationDetails.some((l) => l.locationDetailId === dpLocId)) {
      setLocationDetailId(dpLocId);
    }
  }, [draft, locationDetailId, activeDataPoint, locationDetails]);

  // DEFCAT-FU-05 — preselect the L0 component group from the data point once
  // the groups land (overridable). One-time per draft, like Location Detail.
  // Only preselect if the catalogue actually has locations under that group.
  //
  // PREFILL BUG FIX (Christian 2026-06-04) — root cause of "Component is empty
  // when drawing a new defect": this effect's one-time guard
  // (`componentGroupTouched`) was being BURNED before the catalogue read landed.
  // Two queries race the drawer open: the assets `/component-groups` list (near-
  // static, 10-min staleTime → resolves almost immediately) and the V2 catalogue
  // read (`allEntries`, which feeds `groupsInUse`). The old code set
  // `setComponentGroupTouched(true)` UNCONDITIONALLY the moment the groups list
  // arrived — but `groupsInUse` was still EMPTY (catalogue not yet read), so
  // `groupsInUse.has(g.id)` was false → no selection, yet the guard was spent.
  // When `allEntries` landed a tick later the effect was inert (guard already
  // true) and the Component stayed empty. The unit tests masked it because their
  // mocked promises resolve both queries in the same microtask, so `groupsInUse`
  // was already populated when the effect first ran.
  //
  // Fix: do NOT burn the guard until we can actually RESOLVE the prefill —
  // i.e. wait until `groupsInUse` reflects a read catalogue. We gate on
  // `allEntries` (the raw V2 read) being present so the guard only spends once
  // the data needed for the `groupsInUse.has()` check exists.
  useEffect(() => {
    if (!draft) return;
    if (componentGroupTouched) return;
    // Wait for ALL inputs the preselect depends on, so the one-time guard
    // never burns on a half-loaded state (the live race above):
    //   • the groups list (id ⇆ groupCode lookup),
    //   • the catalogue read (`groupsInUse`, derived from `allEntries`), and
    //   • the data point itself (round-3 fix, Christian 2026-06-04: on the
    //     FIRST image of a DA the data-points query is still in flight when
    //     the drawer opens — `activeDataPoint` is null, so the old code
    //     prefilled nothing yet burned the guard → "wrong data" on the
    //     initially-shown image). An image with genuinely no data point
    //     simply never prefills; manual picks set the guard via onChange.
    if (!activeDataPoint) return;
    if ((componentGroupsQuery.data?.items ?? []).length === 0) return;
    if (allEntries.length === 0) return;
    const code = activeDataPoint.componentGroupCode;
    const g = code
      ? (componentGroupsQuery.data?.items ?? []).find(
          (x) => x.groupCode === code,
        )
      : undefined;
    if (g && groupsInUse.has(g.id)) setSelectedComponentGroupId(g.id);
    setComponentGroupTouched(true);
  }, [
    draft,
    componentGroupTouched,
    activeDataPoint,
    componentGroupsQuery.data,
    allEntries,
    groupsInUse,
  ]);

  // DEFCAT-R2-16 AC3 — preselect the cascade Location from the data point.
  // Mechanism (Christian, doc review): match the data point's
  // `location_detail_id` (a global_location_detail) against catalogue location
  // entries' `globalLocationDetailId`; backfill the cascade to that entry's
  // ancestor path. One-time per draft; only fires once the L0 group is set so
  // the match is scoped correctly, and never overrides a manual pick.
  useEffect(() => {
    if (!draft) return;
    if (locationPrefilled) return;
    if (!activeDataPoint) return; // wait for the data point (round-3 fix)
    if (!selectedComponentGroupId) return; // wait for L0
    if (allEntries.length === 0) return; // wait for the catalogue read
    const match = prefillLocationEntryId(
      allEntries,
      activeDataPoint.locationDetailId ?? null,
      selectedComponentGroupId,
    );
    if (match) {
      const path = locationAncestorPath(allEntries, match);
      if (path.length > 0) setLocationLevelIds(path);
    }
    setLocationPrefilled(true);
  }, [
    draft,
    locationPrefilled,
    selectedComponentGroupId,
    allEntries,
    activeDataPoint,
  ]);

  // -- Severity-by-model prefill (DEFCAT P5) ------------------------------
  // The picked TYPE entry carries a per-class-model default severity
  // (`defaultSeverityByModel`, FIX-1 JSONB — or the bare `defectClassId`
  // pre-remigration). Prefill the Severity radios with that default for the
  // EFFECTIVE class model; switching the DA's class model (or the defect
  // type) re-prefills. The "None" model stays null; a graded model with no
  // resolvable default falls back to its lowest grade (so it's never empty).
  // Stops once the analyst manually picks a grade (`severityTouched`).
  const selectedTypeEntry = useMemo<DefectCatalogueEntryV2 | null>(
    () => typeEntries.find((e) => e.entryId === classId) ?? null,
    [typeEntries, classId],
  );
  useEffect(() => {
    if (!draft) return;
    if (severityTouched) return;
    const next = prefillSeverity(selectedTypeEntry, effectiveClassModel);
    setSeverity((prev) => (prev === next ? prev : next));
  }, [draft, severityTouched, selectedTypeEntry, effectiveClassModel]);

  // -- Notes prefill from the class default-description (DEFCAT-R2-17 AC3) --
  // Picking a class prefills Notes with its `defaultDescription` (current UI
  // language → en fallback). Switching the class before the analyst typed
  // re-prefills; once they type (`noteDirty`) we NEVER overwrite their text.
  useEffect(() => {
    if (!draft) return;
    if (noteDirty) return;
    const next = pickLocalized(selectedTypeEntry?.defaultDescription);
    setNote((prev) => (prev === next ? prev : next));
  }, [draft, noteDirty, selectedTypeEntry]);

  // When the chosen location narrows the valid type set, clear a now-invalid
  // type pick so the analyst can't submit a (location, type) pair the BE
  // would reject against the overlay.
  useEffect(() => {
    if (classId.length === 0) return;
    if (validTypeSet && !validTypeSet.has(classId)) {
      setClassId("");
    }
  }, [validTypeSet, classId]);

  // -- Sync the coarse location-detail from the cascade leaf (R2-16) -------
  // The cascade's deepest pick carries a `globalLocationDetailId` (a row of the
  // 17 global_location_details — the same id space as `location_detail_id`).
  // When the analyst changes the cascade we follow it so the chord auto-lock +
  // submit `locationDetailId` track the chosen side. Falls back to the
  // data-point default until the analyst picks a cascade leaf with a rollup.
  const cascadeLeafGld = useMemo<string | null>(() => {
    if (locationEntryId.length === 0) return null;
    const e = locationEntriesForGating.find(
      (x) => x.entryId === locationEntryId,
    );
    return e?.globalLocationDetailId ?? null;
  }, [locationEntryId, locationEntriesForGating]);
  useEffect(() => {
    if (!draft) return;
    if (!cascadeLeafGld) return;
    if (cascadeLeafGld === locationDetailId) return;
    // Only adopt it when the coarse list actually carries that detail, else the
    // MUI value would be out-of-range (and chord auto-lock can't resolve it).
    if (locationDetails.some((l) => l.locationDetailId === cascadeLeafGld)) {
      setLocationDetailId(cascadeLeafGld);
    }
  }, [draft, cascadeLeafGld, locationDetailId, locationDetails]);

  // -- Auto-lock chord% on Leading/Trailing edge --------------------------
  const selectedLocationDetail = useMemo<LocationDetail | null>(
    () =>
      locationDetails.find((l) => l.locationDetailId === locationDetailId) ??
      null,
    [locationDetails, locationDetailId],
  );
  // SB6-ANA-DEF-01 (Christian 2026-05-31) — chord% is PREFILLED on
  // Leading/Trailing edge selection (0 / 100) but stays user-editable.
  // The effect runs whenever the selected location flips between LE and
  // TE; once the user manually drags the slider or types in the field,
  // subsequent renders of the same isLE/isTE state are no-ops because
  // the deps don't change.
  //
  // **useLayoutEffect** (not useEffect) — this fires SYNCHRONOUSLY after
  // the DOM commit but BEFORE the browser paints, closing the
  // interaction window where a user could click Save with chord still
  // stuck at the neutral default 50 from the form-reset effect. Christian
  // saw the race "sometimes the chord is 50% on LE" 2026-05-31; the
  // async useEffect was the cause.
  // Round 3 — fall back to the CASCADE pick itself when the coarse detail
  // can't resolve (see the EdgeNamed comment above). The cascade entry for
  // "Leading Edge" carries the same LE/TE abbreviation + name.
  const selectedCascadeLocationEntry =
    useMemo<DefectCatalogueEntryV2 | null>(() => {
      if (locationEntryId.length === 0) return null;
      return (
        locationEntriesForGating.find((e) => e.entryId === locationEntryId) ??
        null
      );
    }, [locationEntriesForGating, locationEntryId]);
  // The cascade pick is the analyst's explicit intent — when IT resolves an
  // edge it wins; the data-point-sourced coarse detail only decides when the
  // cascade pick isn't an edge (or there is none).
  const cascadeLE = isLeadingEdge(selectedCascadeLocationEntry);
  const cascadeTE = isTrailingEdge(selectedCascadeLocationEntry);
  const isLE =
    cascadeLE || (!cascadeTE && isLeadingEdge(selectedLocationDetail));
  const isTE =
    cascadeTE || (!cascadeLE && isTrailingEdge(selectedLocationDetail));
  useLayoutEffect(() => {
    if (isLE) setChordPercent(0);
    else if (isTE) setChordPercent(100);
  }, [isLE, isTE]);

  const open = draft != null;

  const mutation = useMutation({
    mutationFn: async (body: CreateDefectBody): Promise<Defect> => {
      if (!draft) throw new Error("No draft");
      const url = `/data-acquisitions/${encodeURIComponent(inspectionId)}/images/${encodeURIComponent(draft.imageId)}/defects`;
      const response = await api.post<Defect>(url, body);
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: defectKeys.all });
      setSnackOpen(true);
      reset();
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { message?: string } } };
      setErrorMsg(
        e.response?.data?.message ??
          "Could not save defect. Check the form and try again.",
      );
    },
  });

  const radiusValid = useMemo(() => {
    if (radiusM.trim().length === 0) return false;
    const n = Number(radiusM);
    return Number.isFinite(n) && n >= 0;
  }, [radiusM]);

  const severityValid =
    isNoneClassModel ||
    (severity != null &&
      severityChoices.some((s) => s.classCategory === severity));

  const canSave =
    classId.length > 0 &&
    severityValid &&
    radiusValid &&
    chordPercent >= 0 &&
    chordPercent <= 100 &&
    !mutation.isPending;

  const handleSave = () => {
    if (!draft || !canSave) return;
    if (!isGradingReady()) return;
    setErrorMsg(null);
    const body: CreateDefectBody = {
      defectClassId: classId,
      severity: isNoneClassModel ? null : severity,
      geometry: {
        // ANA-R3-GEOM-03 — send the FULL free-form pixel path the editor
        // produced. Rect mode emits a closed quad (first corner repeated, 5
        // pts), polygon a closed ring, polyline an open line — the BE derives
        // bbox + geometry_origin=manual server-side, so we never send those.
        path: draft.pathPx,
        ...(locationDetailId.length > 0
          ? {
              locationDetailId,
              locationTypeCode: selectedLocationDetail?.typeCode ?? null,
            }
          : {}),
        chordPercent,
        radiusM: Number(radiusM),
      },
      ...(note.length > 0 ? { note } : {}),
      // DEFCAT P5 slice 2 — the fine catalogue location (axis='location').
      // Top-level per the createDefect contract (lyse@7.6.0), alongside the
      // coarse geometry.locationDetailId above.
      ...(locationEntryId.length > 0 ? { locationEntryId } : {}),
      validation,
    };
    mutation.mutate(body);
  };

  const handleCancel = () => {
    setDraft(null); // keeps draw mode on, just dismisses the drawer
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCancel}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 380 },
            p: 2,
          },
          // Story 023 AC13 — focus trap + dialog role
          role: "dialog",
          "aria-label": "New defect",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography
            component="h2"
            sx={{
              fontSize: typography.size["title-md"],
              fontWeight: typography.weight.semibold,
              letterSpacing: typography.tracking["title-md"],
            }}
          >
            New defect
          </Typography>
          <IconButton
            size="small"
            onClick={handleCancel}
            aria-label="Close drawer"
          >
            <XIcon fontSize="small" />
          </IconButton>
        </Stack>

        {errorMsg ? (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {errorMsg}
          </Alert>
        ) : null}

        {/* Round 3 — inter-group spacing halved from the first cut's 24px
            (Christian: "Spacing between group boxes is also quite big —
            reduce by 50%"). 12px still separates the cards cleanly because
            each carries its own border + fill. */}
        <Stack spacing={1.5}>
          {/* SB6-ANA-DEF-01 — when there is genuinely no catalogue (tenant
              has zero catalogues), defect types have nowhere to come from,
              so we surface that explicitly above the form. The Save button
              also stays disabled via `canSave` until a class is picked. */}
          {availableCatalogues.length === 0 ? (
            <Alert severity="warning" role="status">
              No defect catalogue exists for this tenant — defect types come
              from a catalogue, so a defect can&apos;t be created until your
              admin adds one.
            </Alert>
          ) : null}

          {/* DEFCAT P5 slice 3 — active-collection narrowing toggle. Shown
              only when a collection is active; flips both pickers between the
              collection's narrowed set and the full catalogue. */}
          {activeCollection ? (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              data-testid="defect-collection-toggle"
            >
              <Typography variant="caption" color="text.secondary" noWrap>
                Collection: {activeCollection.name}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showFullCatalogue}
                    onChange={(e) => {
                      setShowFullCatalogue(e.target.checked);
                    }}
                    inputProps={{ "aria-label": "Show full catalogue" }}
                  />
                }
                label="Full catalogue"
                labelPlacement="start"
              />
            </Stack>
          ) : null}

          {/* WHERE group — the location-first cascade (DEFCAT-R2-16) + the
              coarse Location Detail. Always rendered as a group so the structure
              reads even on a catalogue with no fine location axis. */}
          <SectionGroup heading="Where">
            {/* DEFCAT-R2-16 — L0 component group (prefilled from the data point,
                overridable — changing it CLEARS the lower levels), then one
                dropdown per deeper location level. Labels come from the
                catalogue's Structure labels (R2-15) with sensible fallbacks. */}
            {componentGroupOptions.length > 0 ? (
              <>
                <FormControl fullWidth size="small" sx={compactFieldSx}>
                  <InputLabel
                    id="defect-component-group-label"
                    shrink
                    sx={fieldLabelSx}
                  >
                    {locationLevelLabel(0, "Component")}
                  </InputLabel>
                  <Select
                    labelId="defect-component-group-label"
                    label={locationLevelLabel(0, "Component")}
                    notched
                    displayEmpty
                    MenuProps={compactMenuProps}
                    value={selectedComponentGroupId}
                    onChange={(e) => {
                      setSelectedComponentGroupId(e.target.value);
                      setComponentGroupTouched(true);
                      // R2-16 AC2 — changing L0 clears the dependent levels, and
                      // the prefill must not re-fire against the new group.
                      setLocationLevelIds([]);
                      setLocationPrefilled(true);
                    }}
                    data-testid="defect-component-group-select"
                  >
                    <MenuItem value="">All component groups</MenuItem>
                    {componentGroupOptions.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* One dropdown per deeper location level (depth 0..N). Hidden
                    until the L0 group is chosen; a deeper level only enables once
                    its parent above is picked. Picking a location gates the Class
                    options below via effective(L) (R2-12). */}
                {selectedComponentGroupId
                  ? cascadeLevelOptions.map(({ level, options }) => {
                      // The structure label for an in-catalogue location level
                      // (level 1 of the cascade UI = catalogue depth 0 etc.).
                      const label = locationLevelLabel(level + 1, "Location");
                      const value = locationLevelIds[level] ?? "";
                      return (
                        <FormControl
                          key={`loc-level-${String(level)}`}
                          fullWidth
                          size="small"
                          disabled={options.length === 0}
                          sx={compactFieldSx}
                        >
                          <InputLabel
                            id={`defect-location-${String(level)}-label`}
                            shrink
                            sx={fieldLabelSx}
                          >
                            {label}
                          </InputLabel>
                          <Select
                            labelId={`defect-location-${String(level)}-label`}
                            label={label}
                            notched
                            displayEmpty
                            MenuProps={compactMenuProps}
                            value={value}
                            onChange={(e) => {
                              const picked = e.target.value;
                              setLocationPrefilled(true);
                              setLocationLevelIds((prev) => {
                                // Truncate any deeper levels — a new pick at this
                                // level invalidates the children below it.
                                const next = prev.slice(0, level);
                                next[level] = picked;
                                return next;
                              });
                            }}
                            data-testid={`defect-location-level-${String(level)}`}
                          >
                            <MenuItem value="">—</MenuItem>
                            {options.map((o) => (
                              <MenuItem key={o.entryId} value={o.entryId}>
                                {o.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      );
                    })
                  : null}
              </>
            ) : null}

            {/* Round 3 (Christian 2026-06-04) — the coarse Location Detail
                select was REMOVED from the UI: it surfaced the data point's
                `location_detail_id`, which is a property of where the PICTURE
                was taken and must never be edited from the defect form. The
                value still flows invisibly: data-point prefill + cascade-leaf
                rollup (effects above) feed the chord auto-lock and the
                submitted `geometry.locationDetailId`. */}
          </SectionGroup>

          {/* WHAT group. */}
          <SectionGroup heading="What">
            {/* DEFCAT-R2-17 — Class is a TYPEAHEAD: typing filters by name +
              abbreviation, case-insensitive. Options are gated by the chosen
              location (effective(L)). Retired classes are filtered server-side
              by the lyse read, so they never reach `typeOptions`. */}
            <Autocomplete<DefectCatalogueEntryV2>
              size="small"
              options={typeOptions}
              value={typeOptions.find((e) => e.entryId === classId) ?? null}
              disabled={typeOptions.length === 0}
              slotProps={{
                paper: {
                  sx: {
                    "& .MuiAutocomplete-option": {
                      fontSize: typography.size["caption-lg"],
                      minHeight: 32,
                    },
                  },
                },
              }}
              getOptionLabel={(o) => o.name.en}
              isOptionEqualToValue={(a, b) => a.entryId === b.entryId}
              filterOptions={(opts, state) => {
                const q = state.inputValue.trim().toLowerCase();
                if (q.length === 0) return opts;
                return opts.filter((o) => {
                  const name = o.name.en.toLowerCase();
                  const abbr = (o.abbreviation?.en ?? "").toLowerCase();
                  return name.includes(q) || abbr.includes(q);
                });
              }}
              onChange={(_, opt) => {
                setClassId(opt?.entryId ?? "");
              }}
              renderInput={(p) => {
                const { InputLabelProps, InputProps, inputProps, ...rest } = p;
                const tfProps = {
                  ...rest,
                  label: "Class",
                  sx: compactFieldSx,
                  placeholder:
                    typeOptions.length === 0
                      ? typeEntries.length === 0
                        ? availableCatalogues.length === 0
                          ? "No catalogue available"
                          : activeCatalogueId
                            ? "Loading…"
                            : "No catalogue selected"
                        : "No defect types valid at this location"
                      : "Type to filter…",
                  slotProps: {
                    htmlInput: inputProps,
                    input: InputProps,
                    // Item 1+2 — keep the "Class" label SHRUNK on the border even
                    // when empty (a placeholder shows inside instead), and at the
                    // Tier-2 field-label size. Matches the native Select labels.
                    inputLabel: {
                      ...InputLabelProps,
                      shrink: true,
                      sx: fieldLabelSx,
                    },
                  },
                } as unknown as TextFieldProps;
                return <TextField {...tfProps} />;
              }}
              data-testid="defect-class-autocomplete"
            />

            <FormControl>
              <FormLabel id="defect-severity-label" sx={fieldLabelSx}>
                Severity
              </FormLabel>
              {isNoneClassModel ? (
                <RadioGroup
                  row
                  aria-labelledby="defect-severity-label"
                  value="none"
                  data-testid="defect-severity-radio"
                >
                  <FormControlLabel
                    value="none"
                    control={<Radio size="small" disabled />}
                    label="—"
                    slotProps={compactRadioLabelSlot}
                  />
                </RadioGroup>
              ) : (
                <RadioGroup
                  row
                  aria-labelledby="defect-severity-label"
                  value={severity == null ? "" : String(severity)}
                  onChange={(e) => {
                    setSeverity(Number(e.target.value));
                    setSeverityTouched(true);
                  }}
                  data-testid="defect-severity-radio"
                >
                  {severityChoices.length === 0
                    ? // No class model loaded yet — render an empty group.
                      null
                    : severityChoices.map((s) => (
                        <FormControlLabel
                          key={s.defectClassId}
                          value={String(s.classCategory)}
                          control={<Radio size="small" />}
                          label={String(s.classCategory)}
                          slotProps={compactRadioLabelSlot}
                        />
                      ))}
                </RadioGroup>
              )}
            </FormControl>

            {/* DEFCAT FIX-3 — validation toggle. Default 'definitely'; the
              analyst may flag a defect as a 'suspicion' instead. */}
            <FormControl>
              <FormLabel id="defect-validation-label" sx={fieldLabelSx}>
                Validation
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="defect-validation-label"
                value={validation}
                onChange={(e) => {
                  setValidation(e.target.value as DefectValidation);
                }}
                data-testid="defect-validation-radio"
              >
                <FormControlLabel
                  value="definitely"
                  control={<Radio size="small" />}
                  label="Definitely"
                  slotProps={compactRadioLabelSlot}
                />
                <FormControlLabel
                  value="suspicion"
                  control={<Radio size="small" />}
                  label="Suspicion"
                  slotProps={compactRadioLabelSlot}
                />
              </RadioGroup>
            </FormControl>
          </SectionGroup>

          {/* POSITION group — auto-calculated, editable (renamed from GEOMETRY,
              Christian 2026-06-04). */}
          <SectionGroup heading="Position">
            <TextField
              label="Radius (m)"
              type="number"
              size="small"
              fullWidth
              sx={compactFieldSx}
              value={radiusM}
              onChange={(e) => {
                setRadiusM(e.target.value);
              }}
              slotProps={{
                htmlInput: { step: 0.1, min: 0 },
                // Item 1+2 — outlined label sits on the border (the field the
                // brand-book + Christian cite as the reference). Tier-2 size.
                inputLabel: { shrink: true, sx: fieldLabelSx },
              }}
              error={!radiusValid}
              helperText={radiusValid ? "Distance from Rotor axis" : "Required"}
              data-testid="defect-radius-input"
            />

            {/* SB6-ANA-DEF-01 — chord% as Slider + TextField pair (Christian
              2026-05-31). Both inputs stay in sync via shared state.
              Leading/Trailing edge prefills 0 / 100 (handled by the
              useEffect above) but the user can still drag or type to
              override. */}
            <FormControl fullWidth>
              <FormLabel id="defect-chord-label" sx={fieldLabelSx}>
                Chord %
              </FormLabel>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Box sx={{ flex: 1, px: 1 }}>
                  <Slider
                    size="small"
                    value={chordPercent}
                    onChange={(_, v) => {
                      const next = Array.isArray(v) ? (v[0] ?? 0) : v;
                      setChordPercent(Math.max(0, Math.min(100, next)));
                    }}
                    min={0}
                    max={100}
                    step={1}
                    valueLabelDisplay="auto"
                    aria-labelledby="defect-chord-label"
                    data-testid="defect-chord-slider"
                  />
                </Box>
                <TextField
                  size="small"
                  type="number"
                  value={chordPercent}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (Number.isFinite(n)) {
                      setChordPercent(Math.max(0, Math.min(100, n)));
                    }
                  }}
                  slotProps={{
                    htmlInput: {
                      step: 1,
                      min: 0,
                      max: 100,
                      "aria-labelledby": "defect-chord-label",
                    },
                  }}
                  sx={{ ...compactFieldSx, width: 88 }}
                  data-testid="defect-chord-input"
                />
              </Stack>
            </FormControl>
          </SectionGroup>

          {/* NOTES group (last). The Notes field is prefilled from the class
              default-description (R2-17 AC3); the first keystroke marks it dirty
              so a later class change never clobbers the analyst's text. The
              bbox summary (Tier-3 caption) lives here too. */}
          <SectionGroup heading="Notes">
            <TextField
              label="Notes"
              multiline
              minRows={2}
              maxRows={5}
              size="small"
              fullWidth
              sx={compactFieldSx}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                setNoteDirty(true);
              }}
              slotProps={{
                htmlInput: { maxLength: 2000 },
                inputLabel: { shrink: true, sx: fieldLabelSx },
              }}
              data-testid="defect-notes-input"
            />

            {draft && activeImage ? (
              <Typography
                variant="caption"
                color="text.secondary"
                data-testid="defect-geometry-summary"
              >
                Image: {activeImage.basename} · {draft.mode} ·{" "}
                {distinctVertexCount(draft.pathPx)} pts · BBox{" "}
                {Math.round(draft.width)}x{Math.round(draft.height)}
              </Typography>
            ) : null}
          </SectionGroup>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              onClick={handleCancel}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={!canSave}
              data-testid="defect-save"
            >
              {mutation.isPending ? "Saving…" : "Save"}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      <Snackbar
        open={snackOpen}
        autoHideDuration={2000}
        onClose={() => {
          setSnackOpen(false);
        }}
        message="Defect created"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
